<template>
  <div class="product-stage grid grid-cols-1 lg:grid-cols-12">
    <!-- Image gallery / Slider -->
    <NuxtErrorBoundary>
      <ProductStageGallery />
    </NuxtErrorBoundary>

    <!-- Product info -->
    <div class="relative mt-4 lg:col-span-5 lg:col-start-8 lg:mt-0">
      <div class="relative" :class="{ 'pointer-events-none motion-safe:animate-pulse': isLoading }">
        <div class="lg:col-span-2">
          <ProductBadge class="hidden lg:mb-4 lg:inline-flex" />
          <Paragraph
            v-if="product?.manufacturer?.name"
            class="mb-1 font-medium"
            size="small"
            tag="div"
          >
            {{ product?.manufacturer?.name }}
          </Paragraph>
          <div class="flex items-center gap-x-3">
            <Heading size="large" tag="h1" weight="normal">{{ productName }}</Heading>
            <DiscountRateLabel
              v-if="isMobile && hasBulkPrice && activeBulkChild.displayDiscountRate"
              :discount-rate="activeBulkChild.displayDiscountRate"
            />
          </div>
          <Paragraph v-if="productDataDeliveryNote" class="mt-1" size="small" tag="div">
            <span v-html="productDataDeliveryNote"></span>
          </Paragraph>
          <EShopSetPrice v-if="hasEShopSet" class="mt-1 lg:mt-2" />
          <SubscriptionProductPrice
            v-else-if="hasSubscriptionConfiguration && subscriptionChooserActive"
            class="mt-1 lg:mt-2"
          />
          <BulkProductPrice
            v-else-if="hasBulkPrice"
            :active-bulk-child="activeBulkChild"
            :product="product"
            class="mt-1 lg:mt-2"
          />
          <ProductPrice v-else :product="product" class="mt-1 lg:mt-2" />
          <slot name="promoBox"></slot>
          <ProductDeliveryScope
            v-if="deliveryScope"
            :product-delivery-scope="deliveryScope"
            class="mt-4 lg:mt-8"
          />
          <div
            v-if="isPreOrderProduct"
            class="notification is-blue mt-4 items-center gap-4 lg:mt-8"
          >
            <SvgIcon class="notification-icon flex-shrink-0" name="Tooltip" />
            <p v-html="productPreOrderDescription" />
          </div>
          <slot name="introText" />
          <slot name="howTo" />
        </div>

        <div v-if="hasBulkPrice && !hasSubscriptionConfiguration" class="mt-4 space-y-2 md:mt-6">
          <CommonLabel path="BWF/shop/shared/tableQuantity" />
          <span>:</span>
          <SelectField v-model="productQuantity" :options="bulkChooserOptions" />
        </div>

        <div v-if="containsExchangeBox" class="flex flex-col gap-2">
          <ExchangeBoxVariantConfigurator
            v-if="!(exchangeBoxArea?.hideConfigurator === true)"
            class="mt-6 xl:mt-10"
            @change="handleVariantChange"
            @before-change="beforeVariantChange"
          />
          <ProductExchangeBoxInformation class="mt-8">
            <template #depositInfo>
              <slot name="depositInfo" />
            </template>
            <template #priceInfo>
              <slot name="priceInfo" />
            </template>
          </ProductExchangeBoxInformation>
        </div>

        <ProductSubscriptionConfigurator
          v-else-if="hasSubscriptionConfiguration"
          :option-button-default="true"
          :show-option-name="true"
          @change="handleVariantChange"
          @before-subscription-change="beforeVariantChange"
        />

        <ProductVariantConfigurator
          v-else
          :option-button-default="true"
          :show-option-name="true"
          class="mt-6"
          @change="handleVariantChange"
          @before-change="beforeVariantChange"
        />

        <EShopSetConfigurator v-if="hasEShopSet" class="mt-7 xl:mt-10" />

        <div class="mt-7 flex flex-col gap-4 lg:gap-6">
          <ProductAvailability :is-available="isProductAvailable" :product="product" />
          <Tint v-if="containsExchangeBox" class="text-sm" tag="p">
            <CommonLabel path="BWF/shop/productDetailPage/exchangeBox/additionalVAT" />
          </Tint>
          <div v-if="isProductAvailable" ref="addToCartButton">
            <AddToCartButton
              :product="product"
              :product-quantity="productQuantity"
              class="btn btn-blue mt-auto w-full"
              @has-cross-sellings="crossSellingModal.open()"
            >
              <div class="flex items-center leading-5">
                <SvgIcon class="h-4 w-4" name="Cart" />
                <CommonLabel class="ml-2" path="BWF/shop/productDetailPage/addToCartLabel" />
                &nbsp;
                <SharedPrice :value="totalPrice" />
              </div>
            </AddToCartButton>
          </div>
        </div>

        <ShopBenefits v-if="marketSetup?.shopBenefits" class="mt-6 lg:mt-8" />

        <!-- Description and details -->
        <div class="mt-10 lg:col-span-2 lg:col-start-1">
          <div v-if="description" class="product-description-and-details">
            <Heading size="small">
              <CommonLabel path="BWF/shop/productDetailPage/productDetails" />
            </Heading>
            <div class="mt-4">
              <div class="prose" v-html="description" />
            </div>
          </div>
          <p v-if="!hasHiddenMainProduct" class="mt-4 flex gap-2 text-sm text-black">
            <span class="font-medium">
              <CommonLabel path="BWF/shop/productDetailPage/productNumber" />
            </span>
            <span class="font-normal">{{ product?.productNumber }}</span>
          </p>
        </div>

        <div
          v-if="isProductAvailable"
          :class="[
            showStickyAddToCartButton && !isMagnoliaEdit ? 'translate-y-0' : 'translate-y-[150%]',
          ]"
          class="fixed inset-x-0 bottom-0 z-[4] bg-white py-2 shadow-3xl motion-safe:transition-transform lg:py-5"
        >
          <div class="container grid grid-cols-1 lg:grid-cols-12">
            <div class="lg:col-span-5 lg:col-start-8">
              <AddToCartButton
                :product="product"
                :product-quantity="productQuantity"
                class="btn btn-blue mt-auto w-full"
                @has-cross-sellings="crossSellingModal.open()"
              >
                <div class="flex items-center leading-5">
                  <SvgIcon class="h-4 w-4" name="Cart" />
                  <CommonLabel class="ml-2" path="BWF/shop/productDetailPage/addToCartLabel" />
                  &nbsp;
                  <SharedPrice :value="totalPrice" />
                </div>
              </AddToCartButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="isMagnoliaEdit" class="pt-5">Cross Selling Modal</div>
  <ModalComponent :controller="crossSellingModal" :text-centered="true">
    <div class="text-center">
      <CommonLabel path="BWF/shop/productDetailPage/crossSelling/modalHeadline" />
    </div>

    <ProductCrossSelling
      v-if="!isMagnoliaEdit"
      @close-cross-selling-modal="closeCrossSellingModal()"
    ></ProductCrossSelling>
    <div class="mx-auto flex flex-col gap-5">
      <button class="text-link w-full" @click="closeCrossSellingModal()">
        <CommonLabel path="BWF/shop/productDetailPage/crossSelling/modalClose" />
      </button>
    </div>
  </ModalComponent>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { useDebounceFn } from "@vueuse/core";
import { useProductPrice } from "@shopware-pwa/composables-next";
import type { Product } from "@shopware-pwa/types";
import { getProductUrl, getTranslatedProperty } from "@shopware-pwa/helpers-next";
import { Heading } from "~/components/clarity-ui/Heading";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import { Tint } from "~/components/clarity-ui/Tint";
import useMagnoliaContent from "~/composables/useMagnoliaContent";
import { useProductBulkPrices } from "~/composables/shop/useProductBulkPrices";
import { useExchangeBox } from "~/composables/shop/useExchangeBoxProduct";
import { useProductPreOrder } from "~/composables/shop/useProductPreOrder";
import {
  type SubscriptionConfiguration,
  useProductSubscriptionConfigurator,
} from "~/composables/shop/useProductSubscriptionConfigurator";
import { useProductEShopSetConfigurator } from "~/composables/shop/useProductEShopSetConfigurator";
import useItemTracking from "~/composables/tracking/useItemTracking";

import CommonLabel from "~/templates/components/CommonLabel.vue";
import BulkProductPrice from "~/templates/components/shop/product/BulkProductPrice.vue";
import EShopSetConfigurator from "~/templates/components/shop/product/EShopSets/EShopSetConfigurator.vue";
import EShopSetPrice from "~/templates/components/shop/product/EShopSets/EShopSetPrice.vue";
import ExchangeBoxVariantConfigurator from "~/templates/components/shop/product/ExchangeBoxVariantConfigurator.vue";
import ProductBadge from "~/templates/components/shop/product/ProductBadge.vue";
import ProductSubscriptionConfigurator from "~/templates/components/shop/product/ProductSubscriptionConfigurator.vue";
import SubscriptionProductPrice from "~/templates/components/shop/product/SubscriptionProductPrice.vue";
import ShopBenefits from "~/templates/components/shop/product/ShopBenefits.vue";
import DiscountRateLabel from "~/templates/elements/DiscountRateLabel.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import SelectField from "~/templates/elements/form/SelectField.vue";
import AddToCartButton from "~/templates/partials/header/AddToCartButton.vue";
import ModalComponent from "~/templates/components/ModalComponent.vue";
import ProductCrossSelling from "~/templates/components/shop/product/ProductCrossSelling.vue";
import useD2CStructuredData from "~/composables/useD2CStructuredData";

const { isMagnoliaEdit } = useMagnoliaContext();
const { query, fullPath } = useRoute();
const { isMobile } = useDevice();

const crossSellingModal = useModal();
const closeCrossSellingModal = () => {
  crossSellingModal.close();
  isLoading.value = false;
};

const { product, configurator } = useProduct();

const { isPreOrderProduct, productPreOrderDescription } = useProductPreOrder(product);
const { price } = useProductPrice(product);
const { hasBulkPrice, bulkChooserOptions, activeBulkChild, productQuantity } =
  useProductBulkPrices(product);

const { pathVariable } = useMagnoliaContent();

const { marketSetup } = useMarketSetup();
const querySetOptions = ((query?.setOptions ?? "") as string)
  .split(",")
  .filter((option) => option.length > 0);

const {
  eShopSet,
  hasEShopSet,
  eshopSetAvailable,
  eShopSetCurrentSelection,
  isLoadingUpdatedEShopSet,
  hasHiddenMainProduct,
} = await useProductEShopSetConfigurator(querySetOptions);

const { containsExchangeBox, exchangeBoxTotal } = await useExchangeBox();

const isLoading: Ref<boolean> = inject("isLoading", ref(false));

const exchangeBoxArea = inject("exchangeBoxArea", null);

watch(isLoadingUpdatedEShopSet, () => {
  isLoading.value = isLoadingUpdatedEShopSet.value;
});

const addToCartButton: Ref<HTMLElement | null> = ref(null);

const showStickyAddToCartButton = ref(false);

useIntersectionObserver(addToCartButton, ([{ isIntersecting }]) => {
  const positionY = addToCartButton.value?.getBoundingClientRect?.()?.y ?? 0;
  showStickyAddToCartButton.value = !isIntersecting && positionY > 0;
});

const {
  hasSubscriptionConfiguration,
  subscriptionChooserActive,
  subscriptionTotalPrice,
  activeSubscriptionIntervalId,
  intervalConfigurations,
  activeSubscriptionOption,
  handlePreselection,
} = useProductSubscriptionConfigurator(product);

if (hasSubscriptionConfiguration) {
  // subscription configuration preselection
  handlePreselection();
}

/**
 * Reactive Product Data
 */

const productName = computed(() => getTranslatedProperty(product.value.translated, "name"));
const productDataDeliveryNote = computed(() =>
  getTranslatedProperty(
    product.value?.translated?.customFields,
    "brita_product_data_delivery_note",
  ),
);

const deliveryScope = computed(() =>
  getTranslatedProperty(product.value?.translated?.customFields, "brita_product_data_delivery"),
);
const description = computed(() => getTranslatedProperty(product.value, "description"));

const totalPrice = computed(() => {
  if (hasSubscriptionConfiguration.value && subscriptionChooserActive.value) {
    return subscriptionTotalPrice.value;
  }
  if (hasBulkPrice.value && activeBulkChild.value) {
    return activeBulkChild.value?.totalPrice;
  }
  if (containsExchangeBox.value) {
    return exchangeBoxTotal.value;
  }
  if (hasEShopSet.value && eShopSet.value) {
    return eShopSet.value?.eshopSetPriceTotal;
  }
  return price?.value?.totalPrice;
});

const isProductAvailable = computed(
  () => (!hasEShopSet.value && product?.value?.available) || eshopSetAvailable.value,
);

const beforeVariantChange = async () => {
  isLoading.value = true;
};

const emit = defineEmits(["variantChange"]);
const handleVariantChange = async (changedProduct: Partial<Product>) => {
  emit("variantChange", changedProduct);
};

const { trackItemView } = useItemTracking({
  item: product,
  hasEShopSet,
  eShopSet,
  subscriptionChooserActive,
  activeSubscriptionIntervalId,
  subscriptionTotalPrice,
});

// increase debounce value as product changes might take some time and should not be triggered twice
const trackItemDebounce = useDebounceFn(() => trackItemView(), 1000);

const { productSchema } = useD2CStructuredData();

//@ts-ignore
useJsonld(productSchema(product?.value, configurator?.value, totalPrice?.value));

onMounted(() => {
  watch(
    [subscriptionChooserActive, activeSubscriptionIntervalId, product, eShopSet],
    async () => {
      /**
       * UPDATE PDP URLS with vanilla history API as any changes to the path via vue router will cause NUXT to reload page data
       *
       * reloads do not make any sense as data is already loaded at this point.
       * just need representation in the URL
       */
      // extract first segment of fullUrl to get the vanillaurl without a variant
      const [currentPdpUrl] = fullPath.split(pathVariable.value);
      // remove query params if there are any
      const cleanPdpUrl = currentPdpUrl.split("?")[0];

      const currentUrl = new URL(globalThis.location.href);
      if (eShopSetCurrentSelection.value?.length > 0) {
        // eshop set options
        currentUrl.searchParams?.delete("setOptions");
        currentUrl.searchParams?.set("setOptions", eShopSetCurrentSelection.value.join(","));
        // eshopsets shall never add the variant to the url fix pdpUrl / just updates setOptions
      } else {
        // evaluate new variant pdp segment
        const changedPathVariable = product?.value?.seoUrls?.[0]?.seoPathInfo
          ? getProductUrl(product?.value)
          : `/${product?.value?.id}`;
        // check for subscriptionchooser to set the right searchParams
        if (subscriptionChooserActive.value) {
          currentUrl.searchParams?.delete("subscription");
          currentUrl.searchParams?.set("subscription", activeSubscriptionIntervalId?.value);
        } else {
          // clean up any subscription params on inactive subscription
          currentUrl.searchParams?.delete("subscription");
        }
        // replace the url path with the manipulated pdpurl + pathvariable
        currentUrl.pathname = `${cleanPdpUrl}${changedPathVariable}`;
      }
      history?.replaceState(null, "", currentUrl);

      // reset selected bulk amount if subscription configurator is activated
      if (subscriptionChooserActive.value && hasBulkPrice.value) {
        productQuantity.value = activeSubscriptionOption.value?.subscriptionPrice?.quantity;
      }

      // need to wait one tick for updated storage values as updates are triggered by watchers elsewhere
      // there might be multiple changes (url & variant) at once. So we need debounce here
      requestAnimationFrame(() => trackItemDebounce());
    },
    { immediate: true },
  );

  watch(activeSubscriptionIntervalId, () => {
    const changedSubscriptionProductId = intervalConfigurations.value?.find(
      (configurationToLoad: SubscriptionConfiguration) =>
        configurationToLoad.intervalConfigurationId === activeSubscriptionIntervalId.value,
    )?.productId;
    if (changedSubscriptionProductId !== product?.value?.id) {
      beforeVariantChange();
      emit("variantChange", { id: changedSubscriptionProductId });
    }
  });
});
</script>

<style>
.product-stage {
  .product-variant-configurator {
    @apply gap-10;
  }

  .product-description-and-details {
    ul {
      @apply flex flex-col gap-y-3 pl-6;

      list-style-image: url("~/assets/sprite/svg/Checkmark.svg");
    }

    li {
      @apply m-0 pl-2;
    }
  }
}
</style>
